import { ReadAllResponse, uuid } from "@/interfaces";
import { organization_user_status } from "@/models/organization-user.model";
import { store } from "@/lib/store.ts";
import { IndividualWithJoin, IndividualDetail } from "@/app/vendors/individuals/individuals.entity";
import { SubsRepository } from "@/repositories/subs.repository";
import { requirePermission } from "@/lib/permissions";
import { expiresOnOf } from "@/lib/documents";
import { DocumentsRepository } from "@/repositories/documents.repository";
import { api } from "@/lib/api";
import { ApiRequestError, WORKER_CONTRACT_PAGE_SIZE_MAX, type DocumentOwner, type DocumentRecord, type SettableWorkerContractStatus, type WorkerContract, type WorkerContractListQuery } from "@shyne/shared/api";

export type WorkerContractSortKey = WorkerContractListQuery["sort"];

export type IndividualInviteOutcome = { alreadyOnboarded: boolean };

function activeOrganization(): uuid {
  const { activeOrgId } = store.getState().organization;
  if (!activeOrgId) throw new Error("No active organization");
  return activeOrgId;
}

const contractDocuments = (orgIndividualId: uuid): DocumentOwner => ({ kind: "workerContract", workerContract: orgIndividualId });

const isUnreachedPerson = (error: unknown) => error instanceof ApiRequestError && error.code === "no_such_person";

async function workerContractsOfActiveOrganization(status?: organization_user_status): Promise<WorkerContract[]> {
  const actingOrganization = activeOrganization();
  const contracts: WorkerContract[] = [];
  for (let page = 1; ; page++) {
    const { items, total } = await api.workerContracts.list({ actingOrganization, status, page, pageSize: WORKER_CONTRACT_PAGE_SIZE_MAX });
    contracts.push(...items);
    if (items.length < WORKER_CONTRACT_PAGE_SIZE_MAX || contracts.length >= total) return contracts;
  }
}

export const IndividualsRepository = {
  async listByCurrentOrg(
    page = 1,
    pageSize = 10,
    name?: string,
    service?: string,
    status?: organization_user_status,
    sort: WorkerContractSortKey = "name",
    order: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<IndividualWithJoin>> {
    const { items, total } = await api.workerContracts.list({ actingOrganization: activeOrganization(), q: name, service, status: status || undefined, sort, order, page, pageSize });
    return { data: items, count: total };
  },

  async remove(orgIndividualId: uuid): Promise<void> {
    await api.workerContracts.setStatus(orgIndividualId, { status: "suspended" });
  },

  async getDetail(orgIndividualId: uuid): Promise<IndividualDetail> {
    const [contract, documents] = await Promise.all([api.workerContracts.get(orgIndividualId, { actingOrganization: activeOrganization() }), DocumentsRepository.list(contractDocuments(orgIndividualId))]);
    return { contract, serviceIds: contract.services.map((service) => service.id), documents };
  },

  async setStatus(orgIndividualId: uuid, status: SettableWorkerContractStatus): Promise<WorkerContract> {
    return api.workerContracts.setStatus(orgIndividualId, { status });
  },

  async setServices(orgIndividualId: uuid, services: uuid[]): Promise<WorkerContract> {
    return api.workerContracts.setServices(orgIndividualId, { services });
  },

  async inviteIndividual(email: string, services: uuid[] = []): Promise<IndividualInviteOutcome> {
    await requirePermission("individuals.invite");

    try {
      await api.workerContracts.add({ actingOrganization: activeOrganization(), email, services });
      return { alreadyOnboarded: true };
    } catch (error) {
      if (!isUnreachedPerson(error)) throw error;
    }

    await api.invites.send(activeOrganization(), { kind: "worker", email, services });
    return { alreadyOnboarded: false };
  },

  async uploadDocument(orgIndividualId: uuid, file: File, expiresAt?: string | Date): Promise<DocumentRecord> {
    return DocumentsRepository.upload(contractDocuments(orgIndividualId), file, expiresOnOf(expiresAt));
  },

  async deleteDocument(docId: string): Promise<void> {
    await DocumentsRepository.remove(docId);
  },

  async updateDocumentExpiresAt(docId: string, expiresAt: string | Date | null): Promise<DocumentRecord> {
    return DocumentsRepository.setExpiry(docId, expiresOnOf(expiresAt));
  },

  async listDocuments(orgIndividualId: uuid, page = 1, pageSize = 10): Promise<ReadAllResponse<DocumentRecord>> {
    return DocumentsRepository.page(contractDocuments(orgIndividualId), page, pageSize);
  },

  async listForAssignment(): Promise<
    Array<{ id: uuid; name: string; type: "individual"; organizationIndividualId: uuid }>
  > {
    const contracts = await workerContractsOfActiveOrganization(organization_user_status.active);
    return contracts.map((contract) => ({
      id: contract.person.id,
      name: contract.person.name,
      type: "individual" as const,
      organizationIndividualId: contract.id,
    }));
  },

  async listSubsForAssignment(): Promise<
    Array<{ id: uuid; name: string; type: "sub"; organizationSubsId: uuid }>
  > {
    const subcontracts = await SubsRepository.listAllByCurrentOrg();
    return subcontracts.map((subcontract) => ({
      id: subcontract.subcontractor.id,
      name: subcontract.subcontractor.name,
      type: "sub" as const,
      organizationSubsId: subcontract.id,
    }));
  },

  async listAssignableResources(): Promise<{
    individuals: Array<{ id: uuid; name: string; type: "individual"; organizationIndividualId: uuid }>;
    subs: Array<{ id: uuid; name: string; type: "sub"; organizationSubsId: uuid }>;
  }> {
    const [individuals, subs] = await Promise.all([this.listForAssignment(), this.listSubsForAssignment()]);

    return { individuals, subs };
  },
};
